import StateHelper from "./StateHelper";
import {IncludeExclude, PageName, PageState, QueryFlags} from "./types";
import {UrlParser} from "./types";
import dayjs from ".//dayjs";
import { determineStartingDateMode } from "./DateUtil";

const urlParser: UrlParser = {

  getPageFromCurrentLocation : function(location : string) :  string{
    return location.substring(1,location.length).trim();
  },

  extractKeyAndValue: function (str: string) {
    let value = null;
    let valueArray = str.split("=");
    if (valueArray.length > 0) {
      value = { key: valueArray[0], value: valueArray[1] };
    }
    return value;
  },

  parseUrl: function (url: string, setActiveLink: (activeClink:string)=> void) {
    let array = decodeURI(url).split("?");

    //get page name first
    const pageNamePart = array[0];
    const lastIndexOfBar = pageNamePart.lastIndexOf("/");
    const pageName = this.getPageFromCurrentLocation(pageNamePart.substring(lastIndexOfBar));
    const targetPage = pageName ? pageName : PageName.products;

    // Set current page in StateHelper and get that page's state
    StateHelper.setCurrentPage(targetPage);
    const pageState : PageState = StateHelper.getPageState(targetPage);

    // Update page property and active link
    pageState.page = targetPage;
    setActiveLink(pageState.page);

    // If no URL parameters, keep existing state (don't reset)
    if (array.length !== 2) {
      return ;
    }

    let values:{[key:string]:string}[] = [];

    // Reset this page's state to defaults before parsing URL params
    pageState.dateStart = dayjs.utc();
    pageState.dateEnd = dayjs.utc();
    pageState.publisherIds = [];
    pageState.siteIds = [];
    pageState.placementIds = [];
    pageState.trafficSourcesIncludeExclude = IncludeExclude.Include;
    pageState.placementsIncludeExclude = IncludeExclude.Include;
    pageState.trafficTypes = null;
    pageState.trafficTypesIncludeExclude = IncludeExclude.Include;
    pageState.countries = null;
    pageState.countriesIncludeExclude = IncludeExclude.Include;
    pageState.groupBy = null;
    pageState.productIds = null;
    pageState.productsIncludeExclude = IncludeExclude.Include;
    pageState.campaignIds = null;
    pageState.campaignIdsIncludeExclude = IncludeExclude.Include;
    pageState.demandSourceIds = null;
    pageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
    pageState.queryFlags =null;
    pageState.cLinks = null;
    pageState.cLinksIncludeExclude = IncludeExclude.Include;
    pageState.viewAs = null;
    pageState.pivotBy = null;
    pageState.performanceStatsViewMode = null;

    //save keys and values
    let parametersPart = array[1];

    let urlParameters = parametersPart.split("&");
    for (let i = 0; i < urlParameters.length; i++) {
      let str = urlParameters[i];
      let keyValue = this.extractKeyAndValue(str);
      if (keyValue !== null) {
        values.push(keyValue);
      }
    }

    //verify parameters and their names according to what we expect.
    //This is done because parameters can come in any order in the url.
    for (let i = 0; i < values.length; i++) {
      const element = values[i];

      switch (element.key) {
        case "dateStart":
          pageState.dateStart = dayjs.utc(element.value);
          break;
        case "dateEnd":
          pageState.dateEnd  = dayjs.utc(element.value);
          break;
        case "placementsInclude":
        case "placementsExclude":
          // Legacy URL format - parse into new fields
          const elements : string[] = element.value.split(",");
          const publisherIdsSet = new Set<string>();
          const siteIdsArray: string[] = [];
          const placementIdsArray: string[] = [];

          for (let i = 0; i < elements.length; i++) {
            const e : string = elements[i];
            const idsArray : string[] = e.split(":");
            const isPlacement : boolean = idsArray.length > 2;

            if (isPlacement) {
              // Format: publisherId:siteId:placementId
              const placementId = idsArray[2];
              if (placementId && placementId !== "***") {
                placementIdsArray.push(placementId);
              }
            } else {
              // Format: publisherId or publisherId:siteId
              const publisherId = idsArray[0];
              const siteId = idsArray.length > 1 ? idsArray[1] : null;

              if (publisherId && publisherId !== "***") {
                publisherIdsSet.add(publisherId);
              }
              if (siteId && siteId !== "***") {
                siteIdsArray.push(siteId);
              }
            }
          }

          pageState.publisherIds = Array.from(publisherIdsSet);
          pageState.siteIds = siteIdsArray;
          pageState.placementIds = placementIdsArray;

          if (placementIdsArray.length > 0) {
            pageState.placementsIncludeExclude = element.key === "placementsInclude" ? IncludeExclude.Include : IncludeExclude.Exclude;
          } else {
            pageState.trafficSourcesIncludeExclude = element.key === "placementsInclude" ? IncludeExclude.Include : IncludeExclude.Exclude;
          }
        break;
        case "trafficTypesInclude":
          pageState.trafficTypes = element.value;
          pageState.trafficTypesIncludeExclude = IncludeExclude.Include;
          break;
        case "trafficTypesExclude":
          pageState.trafficTypes = element.value;
          pageState.trafficTypesIncludeExclude = IncludeExclude.Exclude;
          break;
        case "countriesInclude":
          pageState.countries = element.value;
          pageState.countriesIncludeExclude = IncludeExclude.Include;
          break;
        case "countriesExclude":
          pageState.countries = element.value;
          pageState.countriesIncludeExclude = IncludeExclude.Exclude;
          break;
        case "groupBy":
          pageState.groupBy = element.value;
          break;
        case "productsInclude":
          pageState.productIds = element.value ? element.value : "";
          pageState.productsIncludeExclude = IncludeExclude.Include;
          break;
        case "productsExclude":
          pageState.productIds = element.value ? element.value : "";
          pageState.productsIncludeExclude = IncludeExclude.Exclude;
          break;
        case "campaignIdsInclude":
          pageState.campaignIds = element.value ? element.value : "";
          pageState.campaignIdsIncludeExclude = IncludeExclude.Include;
          break;
        case "campaignIdsExclude":
          pageState.campaignIds = element.value ? element.value : "";
          pageState.campaignIdsIncludeExclude = IncludeExclude.Exclude;
          break;
        case "demandSourceIdsInclude":
          pageState.demandSourceIds = element.value ? element.value : "";
          pageState.demandSourceIdsIncludeExclude = IncludeExclude.Include;
          break;
        case "demandSourceIdsExclude":
          pageState.demandSourceIds = element.value ? element.value : "";
          pageState.demandSourceIdsIncludeExclude = IncludeExclude.Exclude;
          break;
        case "clinksInclude":
          pageState.cLinks = element.value ? element.value.split(",") : [];
          pageState.cLinksIncludeExclude = IncludeExclude.Include;
          break;
        case "clinksExclude":
          pageState.cLinks = element.value ? element.value.split(",") : [];
          pageState.cLinksIncludeExclude = IncludeExclude.Exclude;
          break;
        case "queryFlags":
          pageState.queryFlags = [];
          if (element.value) {
            element.value.split(",").forEach((qf) => {
              const newQueryFlag = Object.values(QueryFlags).find((value) => value === qf);
              if (newQueryFlag) {
                pageState.queryFlags.push(newQueryFlag);
              }
            });
          }
          break;
        case "viewAs":
          pageState.viewAs = element.value;
          break;
        case "pivotBy":
          pageState.pivotBy = element.value;
          break;
        case "viewMode":
          pageState.performanceStatsViewMode = element.value as 'trafficSource' | 'demand';
          break;
        case "publisherIdsInclude":
        case "publisherIdsExclude":
          pageState.publisherIds = element.value ? element.value.split(",") : [];
          pageState.trafficSourcesIncludeExclude = element.key === "publisherIdsInclude" ? IncludeExclude.Include : IncludeExclude.Exclude;
          break;
        case "siteIdsInclude":
        case "siteIdsExclude":
          pageState.siteIds = element.value ? element.value.split(",") : [];
          pageState.trafficSourcesIncludeExclude = element.key === "siteIdsInclude" ? IncludeExclude.Include : IncludeExclude.Exclude;
          break;
        case "placementIdsInclude":
        case "placementIdsExclude":
          pageState.placementIds = element.value ? element.value.split(",") : [];
          pageState.placementsIncludeExclude = element.key === "placementIdsInclude" ? IncludeExclude.Include : IncludeExclude.Exclude;
          break;
      }

      const groupByArr = pageState.groupBy == null ? [] : pageState.groupBy.split(",");
      pageState.dateMode = determineStartingDateMode(groupByArr, pageState.dateStart, pageState.dateEnd);
    }
  },
};


export default urlParser;
